import { motion } from "framer-motion";
import { useState } from "react";
import { brandDirectory, orderedBrandDirectory } from "../data/brandDirectory";
import { useLocale } from "../i18n/LocaleContext";
import { buildLocalizedPath } from "../lib/siteRouting";
import { BrandMark } from "./BrandMark";
import { ProductShowcaseCarousel } from "./ProductShowcaseCarousel";

const spotlightLogos = [
  "ROUND LAB",
  "CNP",
  "belif",
  "BEYOND",
  "ILSO",
  "JMSOLUTION",
  "SNP",
  "VT",
  "melomeli",
];
const brandVisuals: Record<string, { image: string; tag: string }> = {
  melomeli: { image: "/melomeli-water-01.jpg", tag: "Owned line" },
  "ROUND LAB": { image: "/official-brands/roundlab.jpg", tag: "Campaign visual" },
  ILSO: { image: "/yomei-materials/ilso-book.png", tag: "Brand material" },
  CNP: { image: "/official-brands/cnp-toner.png", tag: "Clinical skincare" },
  JMSOLUTION: { image: "/official-brands/jmsolution-logo.png", tag: "Brand mark" },
  MEDICUBE: { image: "/official-brands/medicube-logo.png", tag: "Brand mark" },
  MEDIHEAL: { image: "/official-brands/mediheal-logo.png", tag: "Brand mark" },
  VT: { image: "/official-brands/vt-logo.svg", tag: "Brand mark" },
  "JUNG SAEM MOOL": { image: "/official-brands/jsm-logo.png", tag: "Brand mark" },
  belif: { image: "/brand-placeholders/belif.svg", tag: "Hydration brand" },
  BEYOND: { image: "/brand-placeholders/beyond.svg", tag: "Eco beauty brand" },
  SNP: { image: "/brand-placeholders/snp.svg", tag: "Skin solution brand" },
  RATAPLAN: { image: "/yomei-materials/rataplan-book.png", tag: "Brand material" },
  "de noah": { image: "/yomei-materials/denoah-book.png", tag: "Brand material" },
  Calvisana: { image: "/brand-placeholders/calvisana.svg", tag: "Brand card" },
  efilow: { image: "/brand-placeholders/efilow.svg", tag: "Brand card" },
  WENLICI: { image: "/brand-placeholders/wenlici.svg", tag: "Brand card" },
  "THE PALACE SECRET": { image: "/brand-placeholders/the-palace-secret.svg", tag: "Brand card" },
};

export function BrandPortfolio() {
  const { copy, locale } = useLocale();
  const { brands } = copy;
  const isChinese = locale === "zh" || locale === "vi";
  const [activeBrandName, setActiveBrandName] = useState<string>(
    brands.featuredBrands.find((brand) => brand.name === "ROUND LAB")?.name ??
      brands.featuredBrands[0]?.name ??
      ""
  );
  const activeBrand =
    brands.featuredBrands.find((brand) => brand.name === activeBrandName) ?? brands.featuredBrands[0];

  const labels = {
    en: {
      spotlight: "Selected partners",
      studio: "Featured brand",
      markets: "Markets",
      role: "Role",
      owned: "Owned brand",
      positioning: "Positioning",
      material: "Visual asset",
      portfolio: "K-Beauty Portfolio",
      viewBrand: "View Brand",
      viewAll: "View All Brands",
      featuredFor: "Selected for",
    },
    zh: {
      spotlight: "核心合作品牌",
      studio: "主推品牌",
      markets: "市场",
      role: "角色",
      owned: "自有品牌",
      positioning: "定位",
      material: "视觉资料",
      portfolio: "韩国美妆组合",
      viewBrand: "查看品牌",
      viewAll: "查看全部品牌",
      featuredFor: "适合市场",
    },
    ko: {
      spotlight: "핵심 파트너 브랜드",
      studio: "대표 브랜드",
      markets: "시장",
      role: "역할",
      owned: "자사 브랜드",
      positioning: "포지셔닝",
      material: "비주얼 자료",
      portfolio: "K-뷰티 포트폴리오",
      viewBrand: "브랜드 보기",
      viewAll: "전체 브랜드 보기",
      featuredFor: "적합 시장",
    },
    vi: {
      spotlight: "Đối tác trọng tâm",
      studio: "Thương hiệu nổi bật",
      markets: "Thị trường",
      role: "Vai trò",
      owned: "Thương hiệu sở hữu",
      positioning: "Định vị",
      material: "Tư liệu hình ảnh",
      portfolio: "Danh mục K-Beauty",
      viewBrand: "Xem thương hiệu",
      viewAll: "Xem tất cả",
      featuredFor: "Phù hợp cho",
    },
  }[locale];
  const activeVisual = brandVisuals[activeBrand.name] ?? {
    image: "/qiandu-logo.jpg",
    tag: "QIANDU portfolio brand",
  };
  const activeVisualIsPlaceholder = activeVisual.image.includes("brand-placeholders");
  const activeVisualFit =
    activeVisual.image.endsWith(".svg") ||
    activeVisual.image.includes("-logo") ||
    activeVisual.image.includes("brand-placeholders")
      ? "object-contain p-6"
      : "object-cover";
  const getBrandHref = (brandName: string) => {
    const match =
      orderedBrandDirectory.find((item) => item.name.toLowerCase() === brandName.toLowerCase()) ??
      brandDirectory.find((item) => item.name.toLowerCase() === brandName.toLowerCase());
    return match ? buildLocalizedPath(locale, `/brands/${match.slug}`) : undefined;
  };

  return (
    <section id="brands" className="relative px-5 py-24 sm:px-8 lg:px-12">
      <div className="absolute inset-x-0 top-0 h-40 bg-[radial-gradient(circle_at_center,rgba(147,190,205,0.14),transparent_72%)]" />
      <div className="mx-auto max-w-7xl">
        <div className="grid gap-6 lg:grid-cols-[0.88fr_0.12fr] lg:items-end">
          <div className="max-w-[26rem]">
            <p className="text-[0.72rem] uppercase tracking-[0.42em] text-aurora/82">{brands.eyebrow}</p>
            <h2
              className={`mt-4 max-w-[31rem] leading-[1.03] text-[#1f2b31] ${
                isChinese
                  ? "font-sans text-[1.9rem] font-medium tracking-[-0.01em] sm:text-[2.48rem]"
                  : "font-display text-[2.26rem] font-semibold leading-[1.02] sm:text-[3.26rem]"
              }`}
            >
              {brands.title}
            </h2>
            <p className="mt-5 max-w-[15.5rem] text-[0.84rem] leading-7 text-soft/52">{brands.body}</p>
          </div>
          <a
            href={buildLocalizedPath(locale, "/brands")}
            className="inline-flex w-fit rounded-full bg-[linear-gradient(135deg,#89adbb,#9fc3ce)] px-5 py-3 text-[9px] uppercase tracking-[0.34em] text-white shadow-[0_10px_20px_rgba(123,167,184,0.16)] transition hover:-translate-y-0.5 lg:justify-self-end"
          >
            {labels.viewAll}
          </a>
        </div>

        <div className="mt-12 rounded-[2.75rem] border border-[#dbe9f0] bg-[linear-gradient(180deg,rgba(252,254,255,0.84),rgba(241,248,251,0.9))] p-5 shadow-[0_12px_24px_rgba(106,138,151,0.05)] backdrop-blur sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="text-[9px] uppercase tracking-[0.38em] text-aurora/68">{labels.spotlight}</div>
            <div className="text-[8px] uppercase tracking-[0.32em] text-soft/38">{labels.portfolio}</div>
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {spotlightLogos.map((logo) => (
              <a key={logo} href={getBrandHref(logo)} className="block">
                <BrandMark name={logo} />
              </a>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-5 lg:grid-cols-[1.08fr_0.92fr] lg:gap-6">
          <div className="rounded-[2.6rem] border border-[#dce9ef] bg-[linear-gradient(180deg,rgba(252,254,255,0.88),rgba(239,246,249,0.94))] p-3.5 shadow-[0_16px_30px_rgba(106,138,151,0.07)] backdrop-blur sm:p-5">
            <ProductShowcaseCarousel />
          </div>

          <div className="rounded-[2.6rem] border border-[#dce8ee] bg-[linear-gradient(180deg,rgba(252,254,255,0.86),rgba(240,246,249,0.94))] p-5 shadow-[0_14px_26px_rgba(106,138,151,0.06)] backdrop-blur sm:p-6">
            <div className="flex flex-wrap gap-2.5">
              {brands.featuredBrands.slice(0, 6).map((brand) => (
                <button
                  key={brand.name}
                  type="button"
                  onClick={() => setActiveBrandName(brand.name)}
                  className={`rounded-full border px-4 py-2 text-[10px] uppercase tracking-[0.24em] transition ${
                    activeBrand.name === brand.name
                      ? "border-[#acc6d0] bg-[rgba(145,184,198,0.12)] text-[#1f2b31]"
                      : "border-transparent bg-[rgba(250,253,255,0.74)] text-soft/74 hover:bg-white"
                  }`}
                >
                  {brand.name}
                </button>
              ))}
            </div>

            <motion.div
              key={activeBrand.name}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-7 grid gap-5"
            >
              <div className="overflow-hidden rounded-[2.1rem] border border-[#e3edf2] bg-[rgba(255,255,255,0.72)] shadow-[0_10px_22px_rgba(106,138,151,0.05)]">
                <div className="aspect-[1.52/0.88] overflow-hidden bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(242,248,251,0.94))]">
                  {activeVisualIsPlaceholder ? (
                    <div className="flex h-full w-full items-center justify-center px-6 py-8">
                      <div className="w-full max-w-[18rem]">
                        <BrandMark name={activeBrand.name} />
                      </div>
                    </div>
                  ) : (
                    <img
                      src={activeVisual.image}
                      alt={activeBrand.name}
                      className={`h-full w-full object-center ${activeVisualFit}`}
                    />
                  )}
                </div>
                <div className="border-t border-[#dde9ef] px-5 py-4 text-[7px] uppercase tracking-[0.36em] text-soft/40">
                  {labels.material} · {activeVisual.tag}
                </div>
              </div>

              <div>
                <div className="text-[8px] uppercase tracking-[0.38em] text-soft/44">
                  {activeBrand.name.toLowerCase() === "melomeli" ? labels.owned : labels.studio}
                </div>
                <h3 className="mt-3 font-display text-[1.9rem] leading-tight text-[#1f2b31] sm:text-[2.12rem]">
                  {activeBrand.name}
                </h3>
                <p className="mt-4 max-w-[16.5rem] text-[0.86rem] leading-7 text-soft/54">{activeBrand.slogan}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <div className="rounded-full bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(241,247,250,0.98))] px-4 py-2 text-[10px] tracking-[0.18em] text-soft/66 shadow-[0_8px_18px_rgba(106,138,151,0.04)]">
                    <span className="mr-2 text-soft/54">{labels.featuredFor}</span>
                    <span className="text-[#1f2b31]">{activeBrand.markets.join(" / ")}</span>
                  </div>
                  <div className="rounded-full bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(241,247,250,0.98))] px-4 py-2 text-[10px] tracking-[0.18em] text-soft/66 shadow-[0_8px_18px_rgba(106,138,151,0.04)]">
                    <span className="mr-2 text-soft/54">{labels.role}</span>
                    <span className="text-[#1f2b31]">{activeBrand.category}</span>
                  </div>
                  {getBrandHref(activeBrand.name) ? (
                    <a
                      href={getBrandHref(activeBrand.name)}
                      className="rounded-full bg-[linear-gradient(135deg,#88aebb,#9fc8d2)] px-4 py-2 text-[9px] uppercase tracking-[0.3em] text-white shadow-[0_10px_20px_rgba(123,167,184,0.18)]"
                    >
                      {labels.viewBrand}
                    </a>
                  ) : null}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
